// Profile & Settings pages — business info, goals, package templates, theme,
// account deletion. Props-driven (no direct Firestore beyond a couple writes).
import { useState } from "react";
import { clientToday } from "../domain";
import { fmt, getCurrencySymbol } from "../lib/format";
import { LS } from "../lib/firebase";
import { themeTokens, COLOR_PALETTES } from "../lib/theme";
import { Btn, Inp, SD, Modal, Icon, ConfirmButton, useToast, useFormGuard, APP_VERSION } from "../ui/primitives";
import { PhoneField, PackageTemplateForm } from "../ui/forms";
import { FeedbackForm } from "./FeedbackForm";
import { hapticsEnabled, setHapticsEnabled, haptic, isNativeApp } from "../native";
import { CC, COUNTRIES } from "../data/countries";
import { CLIENT_LABELS, SESSION_LABELS, CURRENCIES, PROFESSIONS, SECTORS } from "../data/lists";

export const ProfilePage=({profile,setProfile,theme,onClose,monthlyGoal,setMonthlyGoal,yearlyGoal,setYearlyGoal,onNavigate})=>{
  useFormGuard(); // pause background sync while this form is open
  const toast=useToast();
  const [form,setForm]=useState(profile);
  const [phone,setPhone]=useState(profile.phone||"");
  const [phoneErr,setPhoneErr]=useState("");
  const [ns,setNs]=useState("");
  const [saved,setSaved]=useState(false);
  const [showConfirm,setShowConfirm]=useState(false);
  const [goalM,setGoalM]=useState(monthlyGoal||"");
  const [goalY,setGoalY]=useState(yearlyGoal||"");
  const currency=getCurrencySymbol(profile);
  const [editingTemplateId,setEditingTemplateId]=useState(null);
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);

  const addSvc=()=>{if(!ns.trim()){toast("Enter a service name first.","warning");return;}setForm(p=>({...p,services:[...(p.services||[]),ns.trim()]}));setNs("");};
  const remSvc=(s)=>setForm(p=>({...p,services:p.services.filter(x=>x!==s)}));
  const save=()=>{
    if(phone.trim()&&phone.replace(/^\+\d{1,4}\s*/,"").replace(/\D/g,"").length<5){
      setPhoneErr("Mobile number is too short");
      return;
    }
    setPhoneErr("");
    setProfile({...form,phone});
    setMonthlyGoal(Number(goalM)||0);
    setYearlyGoal(Number(goalY)||0);
    setSaved(true);
    setTimeout(()=>{setSaved(false);onClose&&onClose();onNavigate&&onNavigate("dashboard");},1200);
  };

  const hasChanges=(
    phone!==(profile.phone||"")||
    (Number(goalM)||0)!==(Number(monthlyGoal)||0)||
    (Number(goalY)||0)!==(Number(yearlyGoal)||0)||
    JSON.stringify(form)!==JSON.stringify(profile)
  );

  // Profile completion score
  const completion=(()=>{
    let s=0;
    if(form.name?.trim())s+=20;
    if(phone?.trim())s+=15;
    if(form.profession?.trim())s+=10;
    if(form.businessName?.trim())s+=15;
    if(form.email?.trim())s+=10;
    if(form.city?.trim())s+=10;
    if((form.services||[]).length>0)s+=15;
    if(form.currency)s+=5;
    return Math.min(s,100);
  })();

  return(
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black" style={{fontFamily:"'Playfair Display',serif",color:tc}}>My Profile</h1>
        {onClose&&<button onClick={onClose} className="p-2 rounded-xl" style={{color:mc}}><Icon name="x" size={18}/></button>}
      </div>

      {/* Completion progress */}
      {completion<100&&(
        <div className="rounded-2xl p-4 border" style={{background:isDark?"#0f172a":"#f0fdf4",borderColor:"#86efac"}}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold" style={{color:"#15803d"}}>Profile {completion}% complete</p>
            <span className="text-xs" style={{color:"#16a34a"}}>{completion<50?"Keep going!":completion<80?"Almost there!":"Nearly done! 🎉"}</span>
          </div>
          <div className="h-2 rounded-full mb-2" style={{background:"#bbf7d0"}}>
            <div className="h-full rounded-full transition-all" style={{width:`${completion}%`,background:"#16a34a"}}/>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {!form.businessName?.trim()&&<span className="text-xs px-2 py-0.5 rounded-full" style={{background:"#fef9c3",color:"#854d0e"}}>+ Business name</span>}
            {!form.city?.trim()&&<span className="text-xs px-2 py-0.5 rounded-full" style={{background:"#fef9c3",color:"#854d0e"}}>+ City</span>}
            {!(form.services||[]).length&&<span className="text-xs px-2 py-0.5 rounded-full" style={{background:"#fef9c3",color:"#854d0e"}}>+ Services</span>}
            {!phone?.trim()&&<span className="text-xs px-2 py-0.5 rounded-full" style={{background:"#fef9c3",color:"#854d0e"}}>+ Mobile</span>}
          </div>
        </div>
      )}
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Personal Info</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Inp label="Full Name" value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))} theme={theme}/></div>
          <div className="col-span-2"><PhoneField label="Mobile Number" value={phone} onChange={v=>{setPhone(v);setPhoneErr("");}} error={phoneErr} onCountryChange={c=>setForm(p=>({...p,country:p.country||c}))} onCurrencyChange={c=>setForm(p=>(!p.currency?{...p,currency:c}:p))} theme={theme} hint="Used for WhatsApp reminders"/></div>
          <div className="col-span-2"><Inp label="Email" type="email" value={form.email||""} onChange={e=>setForm(p=>({...p,email:e.target.value}))} theme={theme}/></div>
          <div className="col-span-2"><Inp label="Business / Studio Name" value={form.businessName||""} onChange={e=>setForm(p=>({...p,businessName:e.target.value}))} theme={theme} placeholder="Shraddha Wellness Studio"/></div>
          <SD label="Country" value={form.country} onChange={v=>setForm(p=>({...p,country:v,city:""}))} options={COUNTRIES} theme={theme}/>
          <SD label="City" value={form.city} onChange={v=>setForm(p=>({...p,city:v}))} options={form.country?(CC[form.country]||[]):[]} placeholder={form.country?"Select city":"Country first"} theme={theme}/>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>I have</label>
            <select className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{borderColor:isDark?"#334155":"#e5e7eb",background:isDark?"#0f172a":"#f9fafb",color:isDark?"#f1f5f9":"#1f2937"}}
              value={form.clientLabel||"Client"} onChange={e=>setForm(p=>({...p,clientLabel:e.target.value,clientLabelCustom:e.target.value!=="Other"?"":p.clientLabelCustom}))}>
              {CLIENT_LABELS.map(l=><option key={l} value={l}>{l}</option>)}
            </select>
            {(form.clientLabel==="Other")&&(
              <input className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none mt-1" style={{borderColor:isDark?"#334155":"#e5e7eb",background:isDark?"#0f172a":"#f9fafb",color:isDark?"#f1f5f9":"#1f2937"}}
                placeholder="What do you call them?" value={form.clientLabelCustom||""} onChange={e=>setForm(p=>({...p,clientLabelCustom:e.target.value}))}/>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>I provide</label>
            <select className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={{borderColor:isDark?"#334155":"#e5e7eb",background:isDark?"#0f172a":"#f9fafb",color:isDark?"#f1f5f9":"#1f2937"}}
              value={form.sessionLabel||"Session"} onChange={e=>setForm(p=>({...p,sessionLabel:e.target.value,sessionLabelCustom:e.target.value!=="Other"?"":p.sessionLabelCustom}))}>
              {SESSION_LABELS.map(l=><option key={l} value={l}>{l}</option>)}
            </select>
            {(form.sessionLabel==="Other")&&(
              <input className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none mt-1" style={{borderColor:isDark?"#334155":"#e5e7eb",background:isDark?"#0f172a":"#f9fafb",color:isDark?"#f1f5f9":"#1f2937"}}
                placeholder="What do you call them? e.g. Drills, Workshops..." value={form.sessionLabelCustom||""} onChange={e=>setForm(p=>({...p,sessionLabelCustom:e.target.value}))}/>
            )}
          </div>
          <div className="col-span-2">
            <SD label="Currency" value={form.currency||""} onChange={v=>setForm(p=>({...p,currency:v}))} options={CURRENCIES} placeholder="Select currency..." theme={theme}/>
          </div>
        </div>
      </div>
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Professional Info</p>
        <div className="grid grid-cols-2 gap-3">
          <SD label="Profession" value={form.profession} onChange={v=>setForm(p=>({...p,profession:v}))} options={PROFESSIONS} theme={theme}/>
          <SD label="Sector / Industry" value={form.sector} onChange={v=>setForm(p=>({...p,sector:v}))} options={SECTORS} theme={theme}/>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Years of Experience</label>
            <input type="text" inputMode="numeric" pattern="[0-9]*"
              className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
              placeholder="e.g. 5" value={form.yearsExp||""}
              onChange={e=>setForm(p=>({...p,yearsExp:e.target.value.replace(/[^0-9]/g,"")}))}/>
          </div>
        </div>
      </div>
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Revenue Goals</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Monthly Goal</label>
            <input type="number" className="border rounded-xl px-3 py-2.5 text-sm focus:outline-none"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
              placeholder="e.g. 25000" value={goalM} onChange={e=>setGoalM(e.target.value)}/>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Yearly Goal</label>
            <input type="number" className="border rounded-xl px-3 py-2.5 text-sm focus:outline-none"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc,color:tc}}
              placeholder="e.g. 300000" value={goalY} onChange={e=>setGoalY(e.target.value)}/>
          </div>
        </div>
      </div>
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Services Provided</p>
        <div className="flex gap-2">
          <input className="flex-1 border rounded-xl px-4 py-2.5 text-sm focus:outline-none"
            style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:tc}}
            placeholder="Hatha Yoga, Personal Training..." value={ns}
            onChange={e=>setNs(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addSvc()}/>
          <Btn onClick={addSvc} theme={theme}><Icon name="plus" size={16}/></Btn>
        </div>
        <div className="flex flex-wrap gap-2">
          {(form.services||[]).map(s=>(
            <span key={s} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
              style={{background:theme?.light||"#ccfbf1",color:theme?.primary||"#0f766e"}}>
              {s}
              <button onClick={()=>remSvc(s)}><Icon name="x" size={12}/></button>
            </span>
          ))}
          {(!form.services||form.services.length===0)&&<p className="text-xs" style={{color:mc}}>No services yet.</p>}
        </div>
      </div>
      {/* Package Templates */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Package Templates</p>
        {/* Existing templates */}
        <div className="space-y-2">
          {(form.packageTemplates||[]).sort((a,b)=>a.name.localeCompare(b.name)).map((pt,i)=>{
            const isEditing=editingTemplateId===pt.id;
            return(
              <div key={pt.id||i} className="rounded-xl border overflow-hidden" style={{borderColor:isEditing?(theme?.primary||"#0f766e"):bc}}>
                {/* Template row */}
                <div className="flex items-center justify-between p-3" style={{background:isDark?"#0f172a":"#fff"}}>
                  <div>
                    <p className="text-sm font-semibold" style={{color:tc}}>{pt.name}</p>
                    <p className="text-xs mt-0.5" style={{color:mc}}>
                      {pt.packageStyle==="sessions"?`${pt.sessions} ${pt.sessions===1?(form.sessionLabel||"Session"):(form.sessionLabel||"Session")+"s"}`:`${pt.durationDays} days`}
                      {pt.amount>0?` · ${fmt(pt.amount,currency)}`:""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>setEditingTemplateId(isEditing?null:pt.id)}
                      className="p-1.5 rounded-lg transition-all"
                      style={{color:isEditing?(theme?.primary||"#0f766e"):"#6b7280",background:isEditing?(theme?.light||"#ccfbf1"):(isDark?"#1e293b":"#f3f4f6")}}>
                      <Icon name="edit" size={14}/>
                    </button>
                    <ConfirmButton
                      label={<Icon name="trash" size={14}/>}
                      confirmLabel="Delete?"
                      onConfirm={()=>{setForm(p=>({...p,packageTemplates:(p.packageTemplates||[]).filter((_,j)=>j!==i)}));setEditingTemplateId(null);}}
                      danger
                      className="p-1.5 rounded-lg"
                      style={{color:"#ef4444",background:"#fef2f2"}}
                    />
                  </div>
                </div>
                {/* Inline edit form */}
                {isEditing&&(
                  <div className="p-3 border-t space-y-3" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
                    <Inp label="Package Name *" value={pt.name} theme={theme}
                      onChange={e=>setForm(p=>({...p,packageTemplates:p.packageTemplates.map((x,j)=>j===i?{...x,name:e.target.value}:x)}))}/>
                    <div className="flex items-end gap-3">
                      <div className="flex flex-col gap-1" style={{width:"80px"}}>
                        <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>
                          {pt.packageStyle==="sessions"?`${form.sessionLabel||"Session"}s`:"Days"}
                        </label>
                        <input type="text" inputMode="numeric" maxLength={4}
                          className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none w-full text-center font-semibold"
                          style={{background:isDark?"#1e293b":"#fff",borderColor:isDark?"#334155":"#e5e7eb",color:isDark?"#f1f5f9":"#1f2937"}}
                          value={pt.packageStyle==="sessions"?pt.sessions:pt.durationDays}
                          onChange={e=>{
                            const v=e.target.value.replace(/[^0-9]/g,"");
                            setForm(p=>({...p,packageTemplates:p.packageTemplates.map((x,j)=>j===i
                              ?{...x,[pt.packageStyle==="sessions"?"sessions":"durationDays"]:parseInt(v)||0}
                              :x)}));
                          }}/>
                      </div>
                      <div className="flex-1">
                        <Inp label={`Amount (${currency})`} type="text" inputMode="decimal" theme={theme}
                          value={pt.amount||""}
                          onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");setForm(p=>({...p,packageTemplates:p.packageTemplates.map((x,j)=>j===i?{...x,amount:parseFloat(v)||0}:x)}));}}
                          placeholder="e.g. 5000"/>
                      </div>
                    </div>
                    <button onClick={()=>setEditingTemplateId(null)}
                      className="w-full py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-2"
                      style={{background:isDark?"#334155":"#f3f4f6",color:isDark?"#f1f5f9":"#374151"}}>
                      <Icon name="check" size={13}/>Save Changes
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {(!form.packageTemplates||form.packageTemplates.length===0)&&(
            <p className="text-xs" style={{color:mc}}>No package templates yet.</p>
          )}
        </div>
        {/* Add new template */}
        <PackageTemplateForm theme={theme} currency={currency} profile={form}
          onAdd={(pt)=>setForm(p=>({...p,packageTemplates:[...(p.packageTemplates||[]),pt]}))}/>
      </div>
      <Btn onClick={()=>setShowConfirm(true)} theme={theme} className={`w-full justify-center${hasChanges?"":" opacity-50 cursor-not-allowed"}`} disabled={!hasChanges}>
        {saved?<><Icon name="check" size={16}/>Saved!</>:"Save Profile"}
      </Btn>
      {showConfirm&&(
        <Modal title="Save Profile" onClose={()=>setShowConfirm(false)} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm" style={{color:tc}}>Save changes to your profile?</p>
            <div className="flex gap-2">
              <button onClick={()=>setShowConfirm(false)} className="flex-1 py-2 rounded-xl text-sm font-semibold" style={{background:isDark?"#334155":"#f3f4f6",color:isDark?"#f1f5f9":"#374151"}}>Cancel</button>
              <button onClick={()=>{setShowConfirm(false);save();}} className="flex-1 py-2 rounded-xl text-sm font-semibold" style={{background:theme?.primary||"#0f766e",color:"#fff"}}>Confirm</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export const SettingsPage=({theme,colorKey,setColorKey,modeKey,setModeKey,onLogout,onDeleteAccount,monthlyGoal,setMonthlyGoal,yearlyGoal,setYearlyGoal,tourDone,onResetTour,clients,currency,userId,profile})=>{
  const toast=useToast();
  const [showFeedback,setShowFeedback]=useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  const [deleteInput,setDeleteInput]=useState("");
  const [haptics,setHaptics]=useState(hapticsEnabled());

  const exportClients=(format)=>{
    if(!clients||clients.length===0){toast("No clients to export.","warning");return;}
    const rows=clients.map(c=>({
      Name:c.name||"",Mobile:c.mobile||"",Email:c.email||"",
      "Date of Birth":c.dob||"","Joining Date":c.joiningDate||"",
      Country:c.country||"",City:c.city||"",Profession:c.profession||"",
      Service:c.serviceAvailed||"","Package Type":c.package?.type||"",
      "Package Start":c.package?.startDate||"",
      "Classes Remaining":c.classesRemaining||0,"Classes Attended":c.classesAttended||0,
      "Package Fee":`${fmt(c.packagePrice||0,currency||"₹")}`,
      "Amount Paid":`${fmt(c.paid||0,currency||"₹")}`,
      "Balance Due":`${fmt(Math.max(0,(c.packagePrice||0)-(c.paid||0)),currency||"₹")}`,
    }));
    if(format==="csv"){
      const headers=Object.keys(rows[0]);
      const csv=[headers.join(","),...rows.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
      const blob=new Blob([csv],{type:"text/csv"});
      const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=`clients-${clientToday()}.csv`;a.click();
      toast("Clients exported as CSV","success");
    }else{
      const headers=Object.keys(rows[0]);
      const xmlRows=rows.map(r=>`<Row>${headers.map(h=>`<Cell><Data ss:Type="String">${(r[h]||"").toString().replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</Data></Cell>`).join("")}</Row>`).join("");
      const xml=`<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Clients"><Table><Row>${headers.map(h=>`<Cell><Data ss:Type="String">${h}</Data></Cell>`).join("")}</Row>${xmlRows}</Table></Worksheet></Workbook>`;
      const blob=new Blob([xml],{type:"application/vnd.ms-excel"});
      const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=`clients-${clientToday()}.xls`;a.click();
      toast("Clients exported as Excel","success");
    }
  };

  const {isDark,cb,bc,tc,mc}=themeTokens(theme);
  const p=theme?.primary||"#0f766e";

  return(
    <div className="space-y-5">
      {/* Theme */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-4" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Theme & Appearance</p>
        {/* Mode — Light / Dark / System */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:mc}}>Mode</p>
          <div className="flex gap-1 p-1 rounded-xl" style={{background:isDark?"#0f172a":"#f3f4f6"}}>
            {[{key:"light",label:"☀️ Light"},{key:"dark",label:"🌙 Dark"},{key:"system",label:"⚙️ System"}].map(m=>{
              const isActive=(modeKey||"light")===m.key;
              return(
                <button key={m.key} onClick={()=>setModeKey(m.key)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{background:isActive?(isDark?"#1e293b":"#fff"):"transparent",color:isActive?(theme?.primary||"#0f766e"):mc,boxShadow:isActive?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
        {/* Color — all colors available for any mode */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:mc}}>Color</p>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(COLOR_PALETTES).map(([key,c])=>{
              const isSelected=(colorKey||"teal")===key;
              return(
                <button key={key} onClick={()=>setColorKey(key)}
                  className="flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all"
                  style={{borderColor:isSelected?c.primary:(isDark?"#334155":"#e5e7eb"),background:isSelected?(isDark?c.darkLight:c.light):"transparent"}}>
                  <div className="w-7 h-7 rounded-full" style={{background:c.primary}}/>
                  <p style={{color:isSelected?c.primary:mc,fontSize:"9px",fontWeight:600,textAlign:"center",lineHeight:1.2}}>{c.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preferences — native only (Capacitor Haptics is a no-op on the web) */}
      {isNativeApp()&&(
        <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
          <p className="font-semibold text-sm" style={{color:tc}}>Preferences</p>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium" style={{color:tc}}>Haptic feedback</p>
              <p className="text-xs mt-0.5" style={{color:mc}}>Vibration on taps and confirmations</p>
            </div>
            <button role="switch" aria-checked={haptics} aria-label="Haptic feedback"
              onClick={()=>{const v=!haptics;setHaptics(v);setHapticsEnabled(v);if(v)haptic("light");}}
              className="relative w-12 h-7 rounded-full transition-colors shrink-0"
              style={{background:haptics?p:(isDark?"#334155":"#e5e7eb")}}>
              <span className="absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all" style={{left:haptics?"1.5rem":"0.25rem"}}/>
            </button>
          </div>
        </div>
      )}

      {/* Onboarding Tour */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Onboarding Tour</p>
        <p className="text-xs" style={{color:mc}}>The guided tour walks you through the app when you first sign in.</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{color:tc}}>{tourDone?"Tour completed":"Tour not completed"}</p>
            <p className="text-xs mt-0.5" style={{color:mc}}>{tourDone?"You've already seen the tour":"Will show on next login"}</p>
          </div>
          <button onClick={onResetTour}
            className="px-4 py-2 rounded-xl text-xs font-semibold border-2 transition-all"
            style={{borderColor:theme?.primary||"#0f766e",color:theme?.primary||"#0f766e"}}>
            {tourDone?"Replay Tour":"Show Tour Now"}
          </button>
        </div>
      </div>

      {/* Data Management */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Data Backup</p>
        <p className="text-xs" style={{color:mc}}>Export your data before updating the app. Import to restore it after.</p>
        <div className="flex gap-2 flex-wrap">
          <Btn variant="secondary" theme={theme} onClick={()=>{
            const data={
              clients:LS.get("ufcm_clients")||[],
              leads:LS.get("ufcm_leads")||[],
              payments:LS.get("ufcm_payments")||[],
              attendance:LS.get("ufcm_attendance")||[],
              appointments:LS.get("ufcm_appointments")||[],
              profile:LS.get("ufcm_profile")||{},
              exportedAt:new Date().toISOString(),
            };
            const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
            const url=URL.createObjectURL(blob);
            const a=document.createElement("a");
            a.href=url;a.download=`prabzo-backup-${clientToday()}.json`;a.click();
            URL.revokeObjectURL(url);
          }}>
            ⬇️ Export Data
          </Btn>
          <Btn variant="secondary" theme={theme} onClick={()=>{
            const inp=document.createElement("input");
            inp.type="file";inp.accept=".json";
            inp.onchange=(e)=>{
              const file=e.target.files[0];if(!file)return;
              const reader=new FileReader();
              reader.onload=(ev)=>{
                try{
                  const data=JSON.parse(ev.target.result);
                  if(data.clients)LS.set("ufcm_clients",data.clients);
                  if(data.leads)LS.set("ufcm_leads",data.leads);
                  if(data.payments)LS.set("ufcm_payments",data.payments);
                  if(data.attendance)LS.set("ufcm_attendance",data.attendance);
                  if(data.appointments)LS.set("ufcm_appointments",data.appointments);
                  if(data.profile)LS.set("ufcm_profile",data.profile);
                  toast("Data restored successfully! Refreshing...","success",2000);
                  window.location.reload();
                }catch{toast("Invalid backup file.","error");}
              };
              reader.readAsText(file);
            };
            inp.click();
          }}>
            ⬆️ Import Data
          </Btn>
          <div className="flex gap-2">
            <Btn variant="secondary" theme={theme} onClick={()=>exportClients("csv")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export CSV
            </Btn>
            <Btn variant="secondary" theme={theme} onClick={()=>exportClients("excel")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export Excel
            </Btn>
          </div>
        </div>
      </div>

      {/* Feedback */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-sm" style={{color:tc}}>Feedback</p>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:isDark?"#1e293b":"#f3f4f6",color:mc}}>{APP_VERSION}</span>
        </div>
        <p className="text-xs" style={{color:mc}}>Share your experience — what's working, what's not, and what you'd love to see next.</p>
        <Btn variant="secondary" theme={theme} onClick={()=>setShowFeedback(true)}>
          💬 Share Feedback
        </Btn>
      </div>



      {/* Account */}
      <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
        <p className="font-semibold text-sm" style={{color:tc}}>Account</p>
        <div className="grid grid-cols-2 gap-2">
          <ConfirmButton
            label={<span className="flex items-center justify-center gap-2"><Icon name="logout" size={16}/>Sign Out</span>}
            confirmLabel="Sign out?"
            onConfirm={onLogout}
            theme={theme}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold border"
            style={{borderColor:bc,color:tc,background:cb}}
          />
          <Btn variant="danger" onClick={()=>{setShowDeleteConfirm(true);setDeleteInput("");}} theme={theme} className="w-full flex items-center justify-center gap-2">
            <Icon name="trash" size={16}/>Delete Account
          </Btn>
        </div>
        <p className="text-xs" style={{color:mc}}>Deleting your account permanently removes all your data. This cannot be undone.</p>
      </div>

      {showDeleteConfirm && (
        <Modal title="Confirm Account Deletion" onClose={()=>setShowDeleteConfirm(false)} theme={theme}>
          <div className="space-y-3">
            <p className="text-sm" style={{color:tc}}>
              This will permanently delete your account, including all clients, payments, reports, and settings.
              Type "DELETE" below to confirm.
            </p>
            <p className="text-xs font-semibold" style={{color: deleteInput.trim().toUpperCase()==="DELETE" ? "#16a34a" : "#dc2626"}}>
              {deleteInput.trim().toUpperCase()==="DELETE" ? "Ready to delete" : "You must type DELETE exactly to enable delete"}
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e)=>setDeleteInput(e.target.value)}
              className="w-full border rounded-xl px-3 py-2 text-sm"
              style={{background:isDark?"#0f172a":"#fff",borderColor:isDark?"#334155":"#e5e7eb",color:isDark?"#f1f5f9":"#1f2937"}}
              placeholder="Type DELETE to confirm"
            />
            <div className="flex gap-2">
              <button
                onClick={()=>setShowDeleteConfirm(false)}
                className="flex-1 py-2 rounded-xl text-sm font-semibold"
                style={{background:isDark?"#334155":"#f3f4f6",color:isDark?"#f1f5f9":"#374151"}}
              >
                Cancel
              </button>
              <button
                onClick={async()=>{
                  setShowDeleteConfirm(false);
                  try{await onDeleteAccount();}
                  catch(e){
                    if(e?.cancelled||e?.code==="auth/popup-closed-by-user"||e?.code==="auth/cancelled-popup-request"){
                      toast("Deletion cancelled — nothing was removed.","info");
                    }else{
                      console.error("account delete",e);
                      toast("Couldn't delete your account. Please try again.","error");
                    }
                  }
                }}
                disabled={deleteInput.trim().toUpperCase()!="DELETE"}
                className="flex-1 py-2 rounded-xl text-sm font-semibold"
                style={{background:deleteInput.trim().toUpperCase()==="DELETE"?"#ef4444":"#fca5a5",color:"#fff"}}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </Modal>
      )}


      {/* Shri Studio branding footer */}
      <div className="pt-2 pb-4 text-center space-y-1">
        <a href="https://shri.life" target="_blank" className="text-xs hover:underline" style={{color:isDark?"#475569":"#9ca3af"}} rel="noreferrer">Crafted with ❤️ by Shri Studio ↗</a>
        <p className="text-xs" style={{color:isDark?"#475569":"#9ca3af"}}>
          <a href="/terms.html" target="_blank" style={{color:theme?.primary}}>Terms of Service</a>
          {" · "}
          <a href="/privacy.html" target="_blank" style={{color:theme?.primary}}>Privacy Policy</a>

        </p>
      </div>

      {/* Feedback Modal */}
      {showFeedback&&(
        <Modal title="Share Your Feedback" onClose={()=>setShowFeedback(false)} theme={theme}>
          <FeedbackForm theme={theme} onClose={()=>setShowFeedback(false)} userId={userId} profile={profile}/>
        </Modal>
      )}
    </div>
  );
};
